/**
 * Switch Component (Boolean Toggle)
 */

import { v4 as uuidv4 } from 'uuid'

import { IBuilderComponent } from '../../base'
import { formatFromVariableSelect, formatFromVariableText } from '../../format-utils'
import type { VariableSelectValue, VariableTextValue } from '../../types'
import { deepMerge, parseToVariableText } from '../../parser-utils'
import switchTemplate from './switch.template.json'

export interface ISwitchComponentSettings {
  // Data settings
  'switch-data-value': string // Column ID
  'switch-data-label': VariableTextValue
  'switch-data-default-value': VariableSelectValue
  'switch-data-default-value-editable': boolean
  // Behavior settings
  'switch-behavior-required'?: boolean
}

export interface ISwitchComponent extends IBuilderComponent {
  componentId: 'switch'
  groupId: 'form'
  settings: ISwitchComponentSettings
}

/**
 * Switch AI Input
 */
export interface AiSwitchInput {
  columnId: string // Required: maps to table column
  label?: string[] // Format: ["text:Switch Label"] or ["text:Enable ", "cr:feature"]
  defaultValue?: boolean // Default value
  required?: boolean // Is this field required?
}

const SWITCH_DEFAULT_TEMPLATE = switchTemplate as unknown as ISwitchComponentSettings

export class SwitchComp {
  getTemplate(): ISwitchComponentSettings {
    return SWITCH_DEFAULT_TEMPLATE
  }

  /**
   * format switch component settings to AI switch input
   */
  format(settings: ISwitchComponentSettings): AiSwitchInput {
    const result: AiSwitchInput = {
      columnId: settings['switch-data-value'],
    }

    // Label
    const label = formatFromVariableText(settings['switch-data-label'])
    if (label && label.length > 0) {
      result.label = label
    }

    // Default Value (convert from VariableSelectValue to boolean)
    const defaultValue = formatFromVariableSelect(settings['switch-data-default-value'])
    if (defaultValue) {
      // Parse the defaultValue string (e.g., "text:true" or "text:false")
      if (defaultValue.startsWith('text:')) {
        const value = defaultValue.replace('text:', '')
        result.defaultValue = value === 'true'
      }
    }

    // Required
    if (settings['switch-behavior-required'] !== undefined) {
      result.required = settings['switch-behavior-required']
    }

    return result
  }

  /**
   * parse AI switch input to switch component settings
   */
  parse(input: AiSwitchInput): ISwitchComponentSettings {
    const result = deepMerge(SWITCH_DEFAULT_TEMPLATE, {})

    // Data - Column ID (required for form fields)
    if (input.columnId) {
      result['switch-data-value'] = input.columnId
    }

    // Data - Label
    if (input.label) {
      result['switch-data-label'] = parseToVariableText(input.label)
    }

    // Data - Default Value
    if (input.defaultValue !== undefined) {
      result['switch-data-default-value'] = {
        flag: 2048, // CustomText flag
        id: uuidv4(),
        value: input.defaultValue ? 'true' : 'false',
      }
    }

    return result
  }
}

